import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { REPLY_TO_NONE, defaultConsentHelper } from "@/lib/lifecycleEmail";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import {
  LIFECYCLE_KINDS,
  TEMPLATE_SEEDS,
  WEBHOOK_EVENTS,
  type LifecycleKind,
} from "@/lib/lifecycleEmail";

type Draft = {
  subject: string;
  body: string;
  offer_text: string;
  offer_url: string;
  offer_expires_on: string;
};

type TemplateRow = {
  id: string;
  contest_id: string | null;
  kind: string;
  subject: string;
  body: string;
  offer_text: string | null;
  offer_url: string | null;
  offer_expires_on: string | null;
};

const KIND_LABELS: Record<LifecycleKind, string> = {
  entry_confirmation: "Entry confirmation",
  winner: "Winner",
  non_winner: "Non-winner",
};

function emptyDraft(kind: LifecycleKind): Draft {
  return {
    subject: TEMPLATE_SEEDS[kind].subject,
    body: TEMPLATE_SEEDS[kind].body,
    offer_text: "",
    offer_url: "",
    offer_expires_on: "",
  };
}

function draftFromRow(row: TemplateRow): Draft {
  return {
    subject: row.subject,
    body: row.body,
    offer_text: row.offer_text || "",
    offer_url: row.offer_url || "",
    offer_expires_on: row.offer_expires_on || "",
  };
}

async function authHeader() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Please sign in again");
  return { Authorization: `Bearer ${session.access_token}` };
}

export function LifecycleEmailEditor({
  organizationId,
  contestId,
}: {
  organizationId: string;
  contestId: string;
}) {
  const { toast } = useToast();
  const [drafts, setDrafts] = useState<Record<LifecycleKind, Draft>>({
    entry_confirmation: emptyDraft("entry_confirmation"),
    winner: emptyDraft("winner"),
    non_winner: emptyDraft("non_winner"),
  });
  const [loading, setLoading] = useState(true);
  const [savingKind, setSavingKind] = useState<LifecycleKind | null>(null);
  const [previewHtml, setPreviewHtml] = useState<Partial<Record<LifecycleKind, string>>>({});
  const [previewErrors, setPreviewErrors] = useState<Partial<Record<LifecycleKind, string[]>>>({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("lifecycle_email_templates")
        .select("id, contest_id, kind, subject, body, offer_text, offer_url, offer_expires_on")
        .eq("organization_id", organizationId);
      if (cancelled) return;
      if (error) {
        toast({ title: "Could not load email templates", description: error.message, variant: "destructive" });
        setLoading(false);
        return;
      }
      const rows = (data || []) as TemplateRow[];
      const next = { ...drafts };
      for (const kind of LIFECYCLE_KINDS) {
        const contestRow = rows.find((row) => row.kind === kind && row.contest_id === contestId);
        if (contestRow) next[kind] = draftFromRow(contestRow);
      }
      setDrafts(next);
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
    // Load once per org/contest. Draft edits should not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, contestId]);

  const update = (kind: LifecycleKind, patch: Partial<Draft>) => {
    setDrafts((current) => ({ ...current, [kind]: { ...current[kind], ...patch } }));
  };

  const save = async (kind: LifecycleKind) => {
    const draft = drafts[kind];
    setSavingKind(kind);
    try {
      const payload = {
        subject: draft.subject.trim(),
        body: draft.body,
        offer_text: draft.offer_text.trim() || null,
        offer_url: draft.offer_url.trim() || null,
        offer_expires_on: draft.offer_expires_on || null,
        updated_at: new Date().toISOString(),
      };
      const { data: existing, error: lookupError } = await supabase
        .from("lifecycle_email_templates")
        .select("id")
        .eq("organization_id", organizationId)
        .eq("contest_id", contestId)
        .eq("kind", kind)
        .maybeSingle();
      if (lookupError) throw lookupError;
      const { error } = existing
        ? await supabase.from("lifecycle_email_templates").update(payload).eq("id", existing.id)
        : await supabase.from("lifecycle_email_templates").insert({
          ...payload,
          organization_id: organizationId,
          contest_id: contestId,
          kind,
        });
      if (error) throw error;
      toast({ title: `${KIND_LABELS[kind]} saved` });
    } catch (error: any) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } finally {
      setSavingKind(null);
    }
  };

  const preview = async (kind: LifecycleKind, action: "preview" | "test") => {
    const draft = drafts[kind];
    setSavingKind(kind);
    try {
      const { data, error } = await supabase.functions.invoke("admin-lifecycle-email", {
        body: {
          action,
          contest_id: contestId,
          kind,
          subject: draft.subject,
          body: draft.body,
          offer_text: draft.offer_text,
          offer_url: draft.offer_url,
          offer_expires_on: draft.offer_expires_on || null,
        },
        headers: await authHeader(),
      });
      if (error) throw error;
      if (action === "test") {
        if (data?.error) throw new Error(data.error);
        toast({ title: "Test email sent", description: "Check your inbox." });
        return;
      }
      setPreviewHtml((current) => ({ ...current, [kind]: data?.html || "" }));
      setPreviewErrors((current) => ({ ...current, [kind]: data?.errors || [] }));
      if (data?.blockReason === "missing_postal_address") {
        toast({ title: "Postal address required", description: "Add it in organization settings before sending.", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: action === "test" ? "Test send failed" : "Preview failed", description: error.message, variant: "destructive" });
    } finally {
      setSavingKind(null);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading email templates…</div>;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Until a verified sending domain is added, mail goes out as your From name at hello@send.rafflekit.app. Complaints on that shared domain affect every tenant. The unsubscribe link and postal address are added automatically and cannot be removed.
      </p>
      {LIFECYCLE_KINDS.map((kind) => {
        const draft = drafts[kind];
        const errors = previewErrors[kind] || [];
        return (
          <div key={kind} className="space-y-3 border rounded-lg p-4">
            <h3 className="font-medium">{KIND_LABELS[kind]}</h3>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input value={draft.subject} onChange={(event) => update(kind, { subject: event.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Body</Label>
              <Textarea className="min-h-32" value={draft.body} onChange={(event) => update(kind, { body: event.target.value })} />
              <p className="text-xs text-muted-foreground">Tokens: {"{{first_name}} {{giveaway_name}} {{prize}} {{draw_date}} {{tenant_name}} {{offer_text}} {{offer_expiry}}"}</p>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-2 md:col-span-2">
                <Label>Offer text</Label>
                <Input value={draft.offer_text} onChange={(event) => update(kind, { offer_text: event.target.value })} placeholder="Book a tour" />
                <p className="text-xs text-muted-foreground">This is the text on the offer button.</p>
              </div>
              <div className="space-y-2">
                <Label>Offer expires</Label>
                <Input type="date" value={draft.offer_expires_on} onChange={(event) => update(kind, { offer_expires_on: event.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Offer link</Label>
              <Input value={draft.offer_url} onChange={(event) => update(kind, { offer_url: event.target.value })} placeholder="https://, mailto:, tel:, or sms:" />
              <p className="text-xs text-muted-foreground">
                The button can open a web page, email, phone call, or text message.
                {kind === "entry_confirmation" ? " On the entry confirmation, it is included only when the entrant checks the email box." : ""}
              </p>
            </div>
            {errors.length > 0 && (
              <ul className="text-sm text-red-600 list-disc pl-5">
                {errors.map((error) => <li key={error}>{error}</li>)}
              </ul>
            )}
            {previewHtml[kind] && (
              <iframe title={`${kind} preview`} sandbox="allow-popups allow-popups-to-escape-sandbox" className="w-full h-[36rem] rounded border bg-white" srcDoc={previewHtml[kind]} />
            )}
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => save(kind)} disabled={savingKind === kind}>
                {savingKind === kind && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
              <Button type="button" variant="outline" onClick={() => preview(kind, "preview")} disabled={savingKind === kind}>Preview</Button>
              <Button type="button" variant="outline" onClick={() => preview(kind, "test")} disabled={savingKind === kind || errors.length > 0}>Send test to me</Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function LifecycleOrgSettings({ organizationId }: { organizationId: string }) {
  const { toast } = useToast();
  const [fromName, setFromName] = useState("");
  const [replyMode, setReplyMode] = useState<"support" | "custom" | "none">("support");
  const [replyTo, setReplyTo] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [businessId, setBusinessId] = useState("");
  const [consentHelper, setConsentHelper] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [events, setEvents] = useState<string[]>([...WEBHOOK_EVENTS]);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);

  const load = async () => {
    const [{ data: org }, { data: defaults }, { data: integration }, { data: logs }] = await Promise.all([
      supabase.from("organizations").select("email_from_name, email_reply_to, external_business_id, email_consent_helper").eq("id", organizationId).single(),
      (supabase.from("organizations") as any).select("support_email, sponsor_name").eq("id", organizationId).single(),
      supabase.from("organization_integrations").select("webhook_url, webhook_secret, webhook_events").eq("organization_id", organizationId).maybeSingle(),
      supabase.from("webhook_deliveries").select("id, event, status, response_code, attempts, last_error, created_at").eq("organization_id", organizationId).order("created_at", { ascending: false }).limit(15),
    ]);
    setFromName(org?.email_from_name || "");
    const storedReply = org?.email_reply_to?.trim() || "";
    if (storedReply === REPLY_TO_NONE) {
      setReplyMode("none");
      setReplyTo("");
    } else if (storedReply) {
      setReplyMode("custom");
      setReplyTo(storedReply);
    } else {
      setReplyMode("support");
      setReplyTo("");
    }
    setSupportEmail(defaults?.support_email || "");
    setBusinessId(org?.external_business_id || "");
    setConsentHelper(org?.email_consent_helper || defaultConsentHelper(defaults?.sponsor_name));
    setWebhookUrl(integration?.webhook_url || "");
    setWebhookSecret(integration?.webhook_secret || "");
    setEvents(Array.isArray(integration?.webhook_events) ? integration.webhook_events as string[] : [...WEBHOOK_EVENTS]);
    setDeliveries(logs || []);
  };

  useEffect(() => {
    load().catch((error) => toast({ title: "Could not load email settings", description: error.message, variant: "destructive" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const saveIdentity = async () => {
    setSavingEmail(true);
    try {
      let emailReplyTo: string | null = null;
      if (replyMode === "none") emailReplyTo = REPLY_TO_NONE;
      else if (replyMode === "custom") {
        emailReplyTo = replyTo.trim();
        if (!emailReplyTo.includes("@")) {
          toast({ title: "Enter a reply-to email", variant: "destructive" });
          return;
        }
      }
      const { error } = await supabase.from("organizations").update({
        email_from_name: fromName.trim() || null,
        email_reply_to: emailReplyTo,
        email_consent_helper: consentHelper.trim() || null,
      }).eq("id", organizationId);
      if (error) throw error;
      toast({ title: "Email settings saved" });
      await load();
    } catch (error: any) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } finally {
      setSavingEmail(false);
    }
  };

  const saveWebhook = async () => {
    setSavingWebhook(true);
    try {
      const { error } = await supabase.from("organizations").update({
        external_business_id: businessId.trim() || null,
      }).eq("id", organizationId);
      if (error) throw error;
      const { error: integrationError } = await supabase.from("organization_integrations").upsert({
        organization_id: organizationId,
        webhook_url: webhookUrl.trim() || null,
        webhook_secret: webhookSecret.trim() || null,
        webhook_events: events,
        updated_at: new Date().toISOString(),
      });
      if (integrationError) throw integrationError;
      toast({ title: "Webhook settings saved" });
      await load();
    } catch (error: any) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } finally {
      setSavingWebhook(false);
    }
  };

  const replay = async (deliveryId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("admin-webhook-replay", {
        body: { deliveryId },
        headers: await authHeader(),
      });
      if (error || data?.error) throw new Error(error?.message || data?.error);
      toast({ title: "Replay queued" });
      await load();
    } catch (error: any) {
      toast({ title: "Replay failed", description: error.message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Lifecycle email</CardTitle>
          <CardDescription>These settings apply only to this organization. Each giveaway has its own email copy. The postal address in Contest Defaults is required before any lifecycle email sends.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>From name</Label>
            <Input value={fromName} onChange={(event) => setFromName(event.target.value)} placeholder="Shown instead of RaffleKit" />
          </div>
          <div className="space-y-2">
            <Label>Reply-to</Label>
            <Select value={replyMode} onValueChange={(value) => setReplyMode(value as "support" | "custom" | "none")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="support">Default support email</SelectItem>
                <SelectItem value="custom">Custom address</SelectItem>
                <SelectItem value="none">No reply</SelectItem>
              </SelectContent>
            </Select>
            {replyMode === "custom" && (
              <Input type="email" value={replyTo} onChange={(event) => setReplyTo(event.target.value)} placeholder="replies@yourcompany.com" />
            )}
            <p className="text-xs text-muted-foreground">
              {replyMode === "none"
                ? "These emails are sent with no reply-to address."
                : replyMode === "custom"
                  ? "Replies go to the address above."
                  : supportEmail
                    ? `Replies go to the Default Support Email (${supportEmail}).`
                    : "Replies go to the Default Support Email in Contest Defaults. If that is empty, the email has no reply-to address."}
            </p>
          </div>
          <div className="space-y-2">
            <Label>Default consent wording</Label>
            <Textarea value={consentHelper} onChange={(event) => setConsentHelper(event.target.value)} />
            <p className="text-xs text-muted-foreground">This is the sentence under the email opt-in checkbox. It starts as the text entrants already see, and a giveaway can replace it.</p>
          </div>
        </CardContent>
        <CardFooter>
          <Button type="button" onClick={saveIdentity} disabled={savingEmail}>
            {savingEmail && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save email settings
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Webhooks</CardTitle>
          <CardDescription>Events from this organization are signed and sent only to this URL.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Webhook URL</Label>
            <Input value={webhookUrl} onChange={(event) => setWebhookUrl(event.target.value)} placeholder="https://" />
          </div>
          <div className="space-y-2">
            <Label>Webhook signing secret</Label>
            <div className="flex gap-2">
              <Input value={webhookSecret} onChange={(event) => setWebhookSecret(event.target.value)} />
              <Button type="button" variant="outline" onClick={() => setWebhookSecret(crypto.randomUUID().replace(/-/g, "") + crypto.randomUUID().replace(/-/g, ""))}>Generate</Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>External business id</Label>
            <Input value={businessId} onChange={(event) => setBusinessId(event.target.value)} placeholder="afterlight" />
            <p className="text-xs text-muted-foreground">Sent as business_id on every webhook and in the CSV export, so your CRM can tell which business the entrant belongs to. Leave it blank and we send this organization's id.</p>
          </div>
          <div className="space-y-2">
            <Label>Events</Label>
            {WEBHOOK_EVENTS.map((eventName) => (
              <label key={eventName} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={events.includes(eventName)}
                  onCheckedChange={(checked) => {
                    setEvents((current) => checked ? [...new Set([...current, eventName])] : current.filter((item) => item !== eventName));
                  }}
                />
                {eventName}
              </label>
            ))}
          </div>
          <div className="space-y-3 pt-2">
            <div>
              <h3 className="font-medium">Recent deliveries</h3>
              <p className="text-sm text-muted-foreground">Replay sends the original body again.</p>
            </div>
            {deliveries.length === 0 && <p className="text-sm text-muted-foreground">No deliveries yet.</p>}
            {deliveries.map((delivery) => (
              <div key={delivery.id} className="flex items-center justify-between gap-3 text-sm border rounded-md px-3 py-2">
                <div>
                  <div className="font-medium">{delivery.event}</div>
                  <div className="text-muted-foreground">{delivery.status}{delivery.response_code ? ` · ${delivery.response_code}` : ""}{delivery.last_error ? ` · ${delivery.last_error}` : ""}</div>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={() => replay(delivery.id)}>Replay</Button>
              </div>
            ))}
          </div>
        </CardContent>
        <CardFooter>
          <Button type="button" onClick={saveWebhook} disabled={savingWebhook}>
            {savingWebhook && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save webhook
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

export function LifecycleContestSettings({ organizationId, contestId }: { organizationId: string; contestId: string }) {
  const { toast } = useToast();
  const [listKey, setListKey] = useState("");
  const [captureSource, setCaptureSource] = useState("");
  const [consentHelper, setConsentHelper] = useState("");
  const [fieldMap, setFieldMap] = useState<Record<string, string>>({});
  const [customFields, setCustomFields] = useState<{ field_key: string; label: string }[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [{ data: contest }, { data: fields }, { data: org }] = await Promise.all([
        supabase.from("contests").select("list_key, capture_source, email_consent_helper, outbound_field_map").eq("id", contestId).single(),
        supabase.from("contest_entry_fields").select("field_key, label, field_type").eq("contest_id", contestId).eq("field_type", "custom"),
        (supabase.from("organizations") as any).select("sponsor_name, email_consent_helper").eq("id", organizationId).single(),
      ]);
      const fallback = org?.email_consent_helper?.trim() || defaultConsentHelper(org?.sponsor_name);
      setListKey(contest?.list_key || "");
      setCaptureSource(contest?.capture_source || "");
      setConsentHelper(contest?.email_consent_helper || fallback);
      setFieldMap((contest?.outbound_field_map as Record<string, string>) || {});
      setCustomFields(fields || []);
    };
    load().catch((error) => toast({ title: "Could not load giveaway email settings", description: error.message, variant: "destructive" }));
  }, [contestId, organizationId, toast]);

  const save = async () => {
    setSaving(true);
    try {
      const map: Record<string, string> = {};
      for (const [key, value] of Object.entries(fieldMap)) {
        if (value.trim()) map[key] = value.trim();
      }
      const { error } = await supabase.from("contests").update({
        capture_source: captureSource.trim() || null,
        email_consent_helper: consentHelper.trim() || null,
        outbound_field_map: map,
      }).eq("id", contestId);
      if (error) throw error;
      toast({ title: "Giveaway email settings saved" });
    } catch (error: any) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Entrant capture</CardTitle>
        <CardDescription>List key stays fixed after creation. Email copy for this giveaway is on its Emails page.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>List key</Label>
          <Input value={listKey} readOnly />
        </div>
        <div className="space-y-2">
          <Label>Default capture source</Label>
          <Input value={captureSource} onChange={(event) => setCaptureSource(event.target.value)} placeholder="qr-board-a" />
          <p className="text-xs text-muted-foreground">A ?src= value on the giveaway link overrides this for that entrant.</p>
        </div>
        <div className="space-y-2">
          <Label>Consent wording for this giveaway</Label>
          <Textarea value={consentHelper} onChange={(event) => setConsentHelper(event.target.value)} />
          <p className="text-xs text-muted-foreground">Shown under the email opt-in checkbox for this giveaway. It starts as the organization wording.</p>
        </div>
        {customFields.length > 0 && (
          <div className="space-y-3">
            <Label>Outbound field names</Label>
            {customFields.map((field) => (
              <div key={field.field_key} className="grid gap-2 md:grid-cols-2 items-center">
                <span className="text-sm">{field.label}</span>
                <Input
                  value={fieldMap[field.field_key] || ""}
                  placeholder="Export name, such as event_date"
                  onChange={(event) => setFieldMap((current) => ({ ...current, [field.field_key]: event.target.value }))}
                />
              </div>
            ))}
          </div>
        )}
        <Button type="button" variant="outline" onClick={save} disabled={saving}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save capture settings
        </Button>
      </CardContent>
    </Card>
  );
}
